/* eslint-disable react/prop-types */
/**
 * Admin → Announcements — author planned-outage / maintenance notices.
 *
 * Gated to the app-support admin roles (VITE_ADMIN_ROLE, BD.Admin here,
 * plus the de/admin superusers) — see src/config/appSupport.js. The de-hub
 * `/announcements` write endpoints are the real boundary and 403 everyone
 * else. Non-admins are redirected to `/`, mirroring the other Admin screens.
 *
 * THE DRAFT / PUBLISH SPLIT is the important bit of this screen. Saving is
 * not the same act as broadcasting: an admin writes Friday's outage on
 * Tuesday, checks the wording, and publishes when ready. Publishing fires an
 * all-hands bell notification, and it fires only on the false → true
 * transition — so fixing a typo afterwards is safe and does not re-ping the
 * company. The Publish / Unpublish action is deliberately separate from Edit
 * so nobody broadcasts a half-written notice by reflex.
 *
 * "Interrupt with a modal" (`require_ack`) is offered but flagged as rare.
 * A modal every week trains people to dismiss it unread, which is exactly
 * when you need them to read it.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  NotificationOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import useMeasure from "react-use-measure";
import dayjs from "dayjs";

import {
  WigAntLayout,
  WigAntTable,
} from "@warwick-investment-group/wig-react-library";

import SignIn from "../../signIn";
import { AppContext } from "../../../AppContextProvider";
import useIsAdmin from "../../../hooks/useIsAdmin";
import appSupportService from "../../../services/appSupportService";
import { apiErrorText } from "../../../utils/apiError";
import {
  announcementPhase,
  formatClock,
} from "../../../utils/announcementPhase";

const { Title, Text } = Typography;
const { TextArea } = Input;

const KIND_OPTIONS = [
  { value: "maintenance", label: "Maintenance" },
  { value: "outage", label: "Outage" },
  { value: "info", label: "Info" },
];

const SEVERITY_OPTIONS = [
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "critical", label: "Critical" },
];

const PHASE_META = {
  scheduled: { label: "Scheduled", color: "default" },
  upcoming: { label: "Upcoming", color: "blue" },
  imminent: { label: "Imminent", color: "orange" },
  active: { label: "In progress", color: "red" },
  ended: { label: "Ended", color: "default" },
};

const EMPTY = {
  kind: "maintenance",
  severity: "warning",
  title: "",
  body: "",
  publish_at: null,
  starts_at: null,
  ends_at: null,
  require_ack: false,
};

/** Modal for add / edit. Publishing is a separate action on the row. */
const AnnouncementFormModal = ({
  open,
  mode,
  initialValues,
  saving,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(initialValues || EMPTY);
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values);
    } catch {
      // AntD renders field errors; the parent surfaces API errors.
    }
  };

  return (
    <Modal
      open={open}
      title={mode === "add" ? "New announcement" : "Edit announcement"}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      okText={mode === "add" ? "Save draft" : "Save"}
      destroyOnHidden
      width={640}
    >
      <Form form={form} layout="vertical" initialValues={EMPTY}>
        <Space size={12} style={{ display: "flex" }}>
          <Form.Item label="Type" name="kind" style={{ flex: 1 }}>
            <Select options={KIND_OPTIONS} />
          </Form.Item>
          <Form.Item label="Severity" name="severity" style={{ flex: 1 }}>
            <Select options={SEVERITY_OPTIONS} />
          </Form.Item>
        </Space>

        <Form.Item
          label="Headline"
          name="title"
          rules={[{ required: true, message: "A headline is required" }]}
        >
          <Input
            maxLength={140}
            showCount
            placeholder="e.g. Planned maintenance Saturday 8pm – midnight"
          />
        </Form.Item>

        <Form.Item
          label="Details"
          name="body"
          extra="What will be unavailable, and what people should do about it. Times are shown to each reader in their own timezone."
        >
          <TextArea rows={4} maxLength={2000} showCount />
        </Form.Item>

        <Form.Item
          label="Start showing"
          name="publish_at"
          extra="Leave blank to start showing as soon as it's published."
        >
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>

        <Space size={12} style={{ display: "flex" }}>
          <Form.Item label="Outage starts" name="starts_at" style={{ flex: 1 }}>
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Outage ends"
            name="ends_at"
            style={{ flex: 1 }}
            dependencies={["starts_at"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const start = getFieldValue("starts_at");
                  // Caught server-side too (and by a DB CHECK), but an
                  // inverted window shows to nobody, so it is worth refusing
                  // before the admin walks away believing it is scheduled.
                  if (!value || !start || value.isAfter(start)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("End must be after the start")
                  );
                },
              }),
            ]}
          >
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
        </Space>
        <Text type="secondary" style={{ fontSize: 12 }}>
          The banner stops showing at the outage end time. Leave both blank for
          a notice with no window.
        </Text>

        <Form.Item
          name="require_ack"
          valuePropName="checked"
          style={{ marginTop: 12 }}
        >
          <Checkbox>
            Interrupt with a pop-up until each person acknowledges it
          </Checkbox>
        </Form.Item>
        <Alert
          type="info"
          showIcon
          title="Use the pop-up sparingly"
          description="The banner is what people actually read — it's on screen while they work. A pop-up every week teaches everyone to click through it without reading, so save it for the ones that would genuinely ruin someone's afternoon."
        />
      </Form>
    </Modal>
  );
};

const Announcements = ({ darkMode }) => {
  const { theme, token, getToken, isTokenExpired, user } =
    useContext(AppContext);
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const [ref, bounds] = useMeasure();

  const allowed = isAdmin;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingRow, setEditingRow] = useState(null);

  useEffect(() => {
    if (!allowed) navigate("/");
  }, [allowed, navigate]);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const { token: tk, userOid } = await getAuth();
      if (!tk) return;
      const resp = await appSupportService.listAllAnnouncements({
        token: tk,
        userOid,
      });
      setRows(resp?.data?.data || []);
    } catch (err) {
      console.error("Failed to load announcements:", err?.message || err);
      setErrorMsg(
        apiErrorText(err, { fallback: "Couldn't load announcements." })
      );
    } finally {
      setLoading(false);
    }
  }, [getAuth]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  // dayjs <-> ISO at the edges: the pickers speak dayjs, the API speaks ISO.
  const toPayload = (values) => ({
    kind: values.kind,
    severity: values.severity,
    title: values.title,
    body: values.body || null,
    publish_at: values.publish_at ? values.publish_at.toISOString() : null,
    starts_at: values.starts_at ? values.starts_at.toISOString() : null,
    ends_at: values.ends_at ? values.ends_at.toISOString() : null,
    require_ack: Boolean(values.require_ack),
  });

  const handleCreate = async (values) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await appSupportService.createAnnouncement({
        token: tk,
        userOid,
        // Always a draft. Publishing is a separate, deliberate act.
        announcement: { ...toPayload(values), published: false },
      });
      message.success("Saved as a draft — publish it when you're ready.");
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(apiErrorText(err, { fallback: "Couldn't save that." }));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (values) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await appSupportService.updateAnnouncement({
        token: tk,
        userOid,
        id: editingRow.id,
        updates: toPayload(values),
      });
      message.success("Saved.");
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(apiErrorText(err, { fallback: "Couldn't save that." }));
    } finally {
      setSaving(false);
    }
  };

  const setPublished = async (row, published) => {
    try {
      const { token: tk, userOid } = await getAuth();
      await appSupportService.updateAnnouncement({
        token: tk,
        userOid,
        id: row.id,
        updates: { published },
      });
      message.success(
        published
          ? "Published — everyone has been notified."
          : "Unpublished. It's hidden but not deleted."
      );
      load();
    } catch (err) {
      message.error(apiErrorText(err, { fallback: "Couldn't save that." }));
    }
  };

  const handleDelete = async (row) => {
    try {
      const { token: tk, userOid } = await getAuth();
      await appSupportService.deleteAnnouncement({
        token: tk,
        userOid,
        id: row.id,
      });
      message.success("Deleted.");
      load();
    } catch (err) {
      message.error(apiErrorText(err, { fallback: "Couldn't delete that." }));
    }
  };

  const openAdd = () => {
    setModalMode("add");
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setEditingRow(row);
    setModalOpen(true);
  };

  const modalInitialValues = useMemo(() => {
    if (modalMode === "edit" && editingRow) {
      return {
        kind: editingRow.kind,
        severity: editingRow.severity,
        title: editingRow.title,
        body: editingRow.body || "",
        publish_at: editingRow.publish_at ? dayjs(editingRow.publish_at) : null,
        starts_at: editingRow.starts_at ? dayjs(editingRow.starts_at) : null,
        ends_at: editingRow.ends_at ? dayjs(editingRow.ends_at) : null,
        require_ack: Boolean(editingRow.require_ack),
      };
    }
    return EMPTY;
  }, [modalMode, editingRow]);

  const columns = useMemo(
    () => [
      {
        dataIndex: "title",
        title: "Headline",
        key: "title",
        render: (v, row) => (
          <Space direction="vertical" size={0}>
            <Text strong={row?.published}>{v}</Text>
            {!row?.published && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Draft — nobody can see this yet
              </Text>
            )}
          </Space>
        ),
      },
      {
        dataIndex: "kind",
        title: "Type",
        key: "kind",
        width: 130,
        render: (v) => KIND_OPTIONS.find((k) => k.value === v)?.label || v,
      },
      {
        dataIndex: "severity",
        title: "Severity",
        key: "severity",
        width: 110,
        render: (v) => (
          <Tag
            color={
              v === "critical" ? "red" : v === "warning" ? "orange" : "blue"
            }
          >
            {v}
          </Tag>
        ),
      },
      {
        title: "State",
        key: "phase",
        width: 130,
        render: (_, row) => {
          if (!row?.published) return <Tag>Draft</Tag>;
          const meta = PHASE_META[announcementPhase(row)] || {};
          return <Tag color={meta.color}>{meta.label}</Tag>;
        },
      },
      {
        dataIndex: "starts_at",
        title: "Window",
        key: "starts_at",
        width: 240,
        render: (v, row) =>
          v ? (
            <Text style={{ fontSize: 12 }}>
              {formatClock(v)}
              {row?.ends_at ? ` — ${formatClock(row.ends_at)}` : ""}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        dataIndex: "require_ack",
        title: "Pop-up",
        key: "require_ack",
        width: 90,
        align: "center",
        render: (v) => (v ? <Tag color="purple">Yes</Tag> : <Text type="secondary">—</Text>),
      },
      {
        title: "Actions",
        key: "actions",
        width: 230,
        fixed: "right",
        align: "center",
        render: (_, row) => {
          if (!row) return null;
          return (
            <Space size="small" onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
              >
                Edit
              </Button>
              {row.published ? (
                <Tooltip title="Hide it from everyone without deleting the text">
                  <Button
                    size="small"
                    icon={<EyeInvisibleOutlined />}
                    onClick={() => setPublished(row, false)}
                  >
                    Unpublish
                  </Button>
                </Tooltip>
              ) : (
                <Popconfirm
                  title="Publish and notify everyone?"
                  description="This sends a notification to every user. Editing it afterwards won't notify them again."
                  okText="Publish"
                  onConfirm={() => setPublished(row, true)}
                >
                  <Button
                    size="small"
                    type="primary"
                    icon={<NotificationOutlined />}
                  >
                    Publish
                  </Button>
                </Popconfirm>
              )}
              <Popconfirm
                title="Delete this announcement?"
                description="Gone for good. To retract it without losing the text, unpublish instead."
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleDelete(row)}
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const gridHeight = Math.max(bounds.height - 220, 400);

  if (!allowed) return null;

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row ref={ref}>
          <WigAntLayout
            darkMode={theme === "dark" || darkMode}
            layoutStyle={{
              height: "93vh",
              width: "99vw",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
            contentStyle={{
              minHeight: "70vh",
              overflow: "auto",
              padding: 12,
              margin: 0,
            }}
            showFooter={false}
            showHeader={false}
            siderProps={{ collapsible: true, collapsed: true, width: 0 }}
            showDefaultMenu={false}
            content={
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Title level={3} style={{ margin: 0 }}>
                    Announcements
                  </Title>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openAdd}
                  >
                    New announcement
                  </Button>
                </Row>

                <Alert
                  type="info"
                  showIcon
                  title="These reach everyone, in their own timezone"
                  description="Save a draft first, then publish when you're ready — publishing is what notifies people. This channel only works for PLANNED work: if the app is already down, nobody can load the notice saying it's down."
                />

                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    title="Failed to load announcements"
                    description={errorMsg}
                  />
                )}

                {!loading && rows.length === 0 && !errorMsg ? (
                  <Alert
                    type="info"
                    showIcon
                    title="No announcements yet"
                    description="Click New announcement to schedule one."
                  />
                ) : (
                  <WigAntTable
                    dataSource={rows}
                    columns={columns}
                    rowKey={(record) =>
                      record?.id != null ? String(record.id) : undefined
                    }
                    loading={loading}
                    showSearch
                    searchWidth={300}
                    showActionButton={false}
                    showPagination
                    pagination={{ defaultPageSize: 100, showSizeChanger: true }}
                    tableWidth="100%"
                    scroll={{ y: gridHeight }}
                  />
                )}

                <AnnouncementFormModal
                  open={modalOpen}
                  mode={modalMode}
                  initialValues={modalInitialValues}
                  saving={saving}
                  onCancel={() => setModalOpen(false)}
                  onSubmit={modalMode === "add" ? handleCreate : handleEdit}
                />
              </Space>
            }
          />
        </Row>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default Announcements;
